(function(angular) {
    'use strict';

    angular.module('aprima-provider-api').factory('PatientRepository', [
        '$rootScope',
        'AprimaAnalytics',
        'AprimaRestangular',
        'DeviceSettings',
        'LocalStorageRepository',
        'MetadataRepository',
        'moment',
        'SingleAccessSet',
        PatientRepository
    ]);

    function PatientRepository(
        $rootScope,
        AprimaAnalytics,
        AprimaRestangular,
        DeviceSettings,
        LocalStorageRepository,
        MetadataRepository,
        moment,
        SingleAccessSet
    ) {

        var visitCacheSet = new SingleAccessSet();
        var medicationsCacheSet = new SingleAccessSet();
        var defaultIgnoreCache = DeviceSettings.isPrm;

        $rootScope.$on('visit-updated', function(event, opts){
            visitCacheSet.add(opts.patientId);
            medicationsCacheSet.add(opts.patientId);
        });

        var service = {
            getPatient: getPatient,
            getDemographics: getDemographics,
            geteRxEligibilities: geteRxEligibilities,
            getAllergies: getAllergies,
            getAppointments: getAppointments,
            getPrimaryInsurance: getPrimaryInsurance,
            getProblems: getProblems,
            getDxProblemsWithDisplayOrder: getDxProblemsWithDisplayOrder,
            getActiveProblems: getActiveProblems,
            getMedications: getMedications,
            getRemarks: getRemarks,
            getRecentVisits: getRecentVisits,
            getOrders: getOrders,
            getObservationResults: getObservationResults,
            getVitals: getVitals,
            getObservations: getObservations,
            getHealthMaintenanceReminders: getHealthMaintenanceReminders,
            getObservationValues: getObservationValues,
            createPatient: createPatient,
            getCreatePatientMetadata: getCreatePatientMetadata,
            getMostRecentObservationValues: getMostRecentObservationValues,
            updatePatientPhoto: updatePatientPhoto,
            getPharmacies: getPharmacies,
            getEmVisits: getEmVisits,
            getMedicationsByGroup: getMedicationsByGroup,
            getPatientDetails: getPatientDetails,
            getAccounts: getAccounts,
            getPatientCases: getPatientCases,
            getPatientInformation: getPatientInformation,
            deletePatientPharmacy: deletePatientPharmacy,
            setPharmacyOrder : setPharmacyOrder
        };

        return service;

        function getPatient(uid, restangularOptions, applicationComponent) {
            if(_.isString(restangularOptions) && !applicationComponent) {
                applicationComponent = restangularOptions;
                restangularOptions = null;
            }
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming(applicationComponent || 'PatientRepository', 'api', 'getPatient');

            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients', uid).get().finally(timing.stop);
        }

        function getPatientDetails(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'getPatientDetails');

            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/details').get().finally(timing.stop);
        }

        function getDemographics(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient demographics');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/demographics').get().finally(timing.stop);
        }

        function geteRxEligibilities(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient eRxEligibilities');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/erxeligibilities').get().finally(timing.stop);
        }

        function getAllergies(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient allergies');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/allergies').get().finally(timing.stop);
        }

        function getAppointments(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient appointments');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/appointments').get().finally(timing.stop);
        }

        function getPrimaryInsurance(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient primary insurance');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/primary-insurance').get().finally(timing.stop);
        }

        function getProblems(uid, restangularOptions) {
            return internalGetProblems(uid, true, restangularOptions);
        }
        
        function getActiveProblems(uid, restangularOptions) {
            return internalGetProblems(uid, false, restangularOptions);
        }
        
        function internalGetProblems(uid, includeInactive, restangularOptions){
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient problems');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients', uid).getList('problems', {activeOnly: !includeInactive}).finally(timing.stop);
        }

        function getDxProblemsWithDisplayOrder(patientId, medicationSigId, restangularOptions) {
            var dto = {
                PatientId: patientId,
                MedicationSigId: medicationSigId,
                ActiveOnly: true
            };
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient problems by medication sig id');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular
                .one('patients/' + patientId + '/problems/MedicationSig')
                .get(dto)
                .finally(timing.stop);
        }

        function getMedications(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache || medicationsCacheSet.contains(uid) };

            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient medications');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/medications').get().finally(timing.stop);
        }
        
        function getMedicationsByGroup(uid, restangularOptions) {
            return getMedications(uid, restangularOptions).then(function(medications){
                var result = {
                    medications : medications,
                    medicationsExist : false
                };

                //medications should already be sorted by drug class
                var drugClasses = [],
                    groupValue = 'seed_value',
                    group;

                for (var i = 0; i < medications.length; i++) {
                    var medication = medications[i];

                    if (groupValue !== medication.DrugClassName) {

                        if (group !== undefined) {
                            drugClasses.push(group);
                        }

                        group = {
                            label: medication.DrugClassName,
                            active: false,
                            medications: []
                        };

                        groupValue = group.label;

                    }

                    group.medications.push(medication);

                    if (medication.IsActive) {
                        group.active = true;
                        result.medicationsExist = true;
                    }
                }
                //one last time
                if(group){
                    drugClasses.push(group);
                }

                result.drugClasses = drugClasses;

                return result;
            });
        }

        function getRemarks(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient remarks');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/remarks').get().finally(timing.stop);
        }

        function getRecentVisits(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache || visitCacheSet.contains(uid) };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient visits');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/recentvisits').get().finally(timing.stop);
        }

        function getOrders(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient orders');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/orders').getList().finally(timing.stop);
        }

        function getObservationResults(uid, filter, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient observation results');
            var restangular = AprimaRestangular.get(restangularOptions);
            var params = {
                dataFilter: filter
            };
            return restangular.one('patients', uid).customGET('observationresults', params).finally(timing.stop);
        }

        function getMostRecentObservationValues(uid, filter, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get most recent patient observation values');
            var restangular = AprimaRestangular.get(restangularOptions);
            var params = {
                dataFilter: filter
            };
            return restangular.one('patients', uid).customGET('most-recent-observation-values', params).finally(timing.stop);
        }

        function getObservationValues(patientId, observationOrderId, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient observation values');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients', patientId).one('observationresults', observationOrderId).get().finally(timing.stop);
        }

        function getVitals(uid, vitalsDefaultSettingId, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient vitals');
            var obsDefaultSettingId = vitalsDefaultSettingId || LocalStorageRepository.getPatientVitalsDefaultSettingId() || '';
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular
                .one('patients', uid)
                .customGET('vitals', {
                    observationDefaultSettingId: obsDefaultSettingId,
                })
                .finally(timing.stop);
        }

        function getObservations(uid, filter, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient observations');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients', uid).customGET('observations', {dataFilter: filter}).finally(timing.stop);
        }

        function getHealthMaintenanceReminders(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient health maintenance reminders');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/healthmaintenancereminders').get().finally(timing.stop);
        }

        function getPharmacies(uid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'get patient pharmacies');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patients/' + uid + '/pharmacies').get().finally(timing.stop);
        }

        function createPatient(patient) {
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'createPatient');
            var restangular = AprimaRestangular.get();
            return restangular.all('patients/').post(patient).finally(timing.stop);
        }

        function getCreatePatientMetadata($scope) {
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'getCreatePatientMetadata');
            return MetadataRepository.getMetadata('patientcreation', $scope).finally(timing.stop);
        }

        function updatePatientPhoto(patientId, patientPhoto, applicationComponent) {
            var timing = AprimaAnalytics.startTiming(applicationComponent || 'PatientRepository', 'api', 'updatePatientPhoto');

            return AprimaRestangular.get()
                .one('patients', patientId)
                .one('photo')
                .customPUT(patientPhoto)
                .finally(timing.stop);
        }
        
        function getEmVisits(uid, restangularOptions){
            restangularOptions = restangularOptions || { ignoreCache : defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'getEmVisits');
            var restangular = AprimaRestangular.get();
            return restangular.one('patients/', uid).all('em-visits').getList().finally(timing.stop);
        }

        function getAccounts(patientId) {
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'getAccounts');
            var restangular = AprimaRestangular.get();
            return restangular
                .one('patients', patientId)
                .getList('accounts')
                .finally(timing.stop);
        }

        function getPatientCases(patientId) {
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'getPatientCases');
            var restangular = AprimaRestangular.get();
            return restangular
                .one('patients', patientId)
                .getList('patientcases')
                .finally(timing.stop);
        }

        function getPatientInformation(patientUid, appointmentUid, visitUid, accountUid, restangularOptions) {
            restangularOptions = restangularOptions || { ignoreCache: defaultIgnoreCache };
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'getPatientInformation');
            var restangular = AprimaRestangular.get(restangularOptions);
            return restangular.one('patientInformation/' + patientUid + '/' + appointmentUid + '/' + visitUid + '/' + accountUid).get().finally(timing.stop);
        }

        function deletePatientPharmacy(patientPharmacyUid){
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'DeletePatientPharamciesById');
            var restangular = AprimaRestangular.get();
            return restangular.one('patients/pharmacy/'+patientPharmacyUid).remove().finally(timing.stop);
        }

        function setPharmacyOrder(list,patientId){
            var timing = AprimaAnalytics.startTiming('PatientRepository', 'api', 'SetPatientPharmacySortOrder');
            var restangular = AprimaRestangular.get();
            return restangular.all('patients/'+patientId+'/pharmacy/order').post(list).finally(timing.stop);
            
        }
    }

})(window.angular);
